// Tones and icons of request decisions and enforcement (feature spec 17, phase 2): states are never colour alone.
import { CircleCheck, CircleX, Clock, Loader, ShieldOff, TriangleAlert, type LucideIcon } from 'lucide-react';
import type {
  RequestDecision,
  RequestDecisionKind,
  RequestEnforcement,
  RequestInstance,
  RequestPartName,
} from '../../api/types';

export type RequestTone = 'success' | 'warning' | 'danger' | 'neutral' | 'info';

/** The tone and icon of an instance's enforcement words: never colour alone (spec 11 6.15.21, U2.3). */
export function enforcementTone(
  enforcement: RequestEnforcement,
  enforcing: boolean,
  decision: RequestDecision | null,
): { tone: RequestTone; icon: LucideIcon } {
  if (!enforcing || enforcement === 'not-enforced') return { tone: 'neutral', icon: ShieldOff };
  if (enforcement === 'failed') return { tone: 'danger', icon: CircleX };
  if (enforcement === 'open') return { tone: 'success', icon: CircleCheck };
  if (enforcement === 'opening' || enforcement === 'closing') return { tone: 'info', icon: Loader };
  if (decision === 'rejected' || decision === 'revoked') return { tone: 'neutral', icon: CircleX };
  return { tone: 'warning', icon: TriangleAlert };
}

export function decisionTone(decision: RequestDecision): { tone: RequestTone; icon: LucideIcon } {
  switch (decision) {
    case 'approved':
      return { tone: 'success', icon: CircleCheck };
    case 'rejected':
    case 'revoked':
      return { tone: 'danger', icon: CircleX };
    default:
      return { tone: 'warning', icon: Clock };
  }
}

/** The part a decision acts on: the pending (or rejected) proposed part first, else the applied part. */
export function decisionPart(i: RequestInstance, kind: RequestDecisionKind): RequestPartName | null {
  const can = (decision: RequestDecision | undefined) =>
    kind === 'approve'
      ? decision === 'pending' || decision === 'rejected'
      : kind === 'reject'
        ? decision === 'pending'
        : kind === 'revoke'
          ? decision === 'approved'
          : decision === 'revoked';
  if (i.withdrawnAt) return null;
  if ((kind === 'approve' || kind === 'reject') && can(i.proposed?.decision)) return 'proposed';
  return can(i.applied?.decision) ? 'applied' : null;
}
